import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface NISTCSFState {
  enabled: "enabled" | "disabled";
}

const defaultNISTCSF: NISTCSFState = {
  enabled: "disabled",
};

export default function NISTCSFModule() {
  const [NISTCSF, setNISTCSF] = useState<NISTCSFState>(defaultNISTCSF);
  const [NISTCSFInitial, setNISTCSFInitial] = useState<NISTCSFState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchNISTCSF = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const nistCSFSettings = settings?.["nistcsf"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof nistCSFSettings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(nistCSFSettings.Enabled);
        } else if (typeof nistCSFSettings.enabled !== 'undefined') {
          isEnabled = checkEnabled(nistCSFSettings.enabled);
        } else if (typeof nistCSFSettings.active !== 'undefined') {
          isEnabled = checkEnabled(nistCSFSettings.active);
        } else if (typeof nistCSFSettings.Active !== 'undefined') {
          isEnabled = checkEnabled(nistCSFSettings.Active);
        }
        setNISTCSF({ enabled: isEnabled ? "enabled" : "disabled" });
        setNISTCSFInitial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch NIST CSF settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchNISTCSF();
  }, []);

  const nistCSFChanged = NISTCSFInitial && JSON.stringify(NISTCSF) !== JSON.stringify(NISTCSFInitial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        nistcsf: { Enabled: NISTCSF.enabled === "enabled" },
      });
      setNISTCSFInitial({ ...NISTCSF });
      toast.success("NIST CSF settings saved");
    } catch (err) {
      toast.error("Failed to save NIST CSF settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="NIST CSF">🧩</span>
        </div>
        <div>
          <div className="font-semibold text-lg">NIST CSF</div>
          <div className="text-xs text-muted-foreground">Cybersecurity Framework</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={NISTCSF.enabled}
          onValueChange={val => setNISTCSF(v => ({ ...v, enabled: val as NISTCSFState["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!nistCSFChanged || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save NIST CSF Settings"}
        </Button>
      </div>
    </div>
  );
}
